import { useState } from "react";
import { useLanguage } from "./LanguageContext";

export default function RSVP() {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    name: "",
    attendance: "attending",
    plusOne: "no",
    plusOneName: "",
    foodChoice: "",
    plusOneFoodChoice: "",
    children: "0",
    dietary: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");

    try {
      const res = await fetch("/api/rsvp.php?action=submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      const data = await res.json();

      if (!res.ok || data.error) {
        throw new Error(data.error || "Something went wrong");
      }

      setSubmitted(true);
    } catch (err) {
      setError(err.message || "Failed to submit RSVP. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section id="rsvp" style={{
        padding: "80px 20px",
        background: "linear-gradient(180deg, var(--blue-50) 0%, white 100%)",
        textAlign: "center",
      }}>
        <div style={{
          maxWidth: "500px",
          margin: "0 auto",
          padding: "48px 32px",
          background: "white",
          borderRadius: "16px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        }}>
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" style={{ margin: "0 auto 16px" }}>
            <circle cx="12" cy="12" r="10" fill="var(--blue-100)" stroke="var(--blue-500)" strokeWidth="1.5" />
            <path d="M8 12l2.5 2.5L16 9" stroke="var(--blue-600)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <h3 style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: "1.8rem",
            color: "var(--blue-800)",
            marginBottom: "12px",
          }}>
            {t.rsvpThankYou}
          </h3>
          <p style={{ color: "#64748b", lineHeight: 1.6 }}>
            {t.rsvpReceived(form.name)}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section id="rsvp" style={{
      padding: "80px 20px",
      background: "linear-gradient(180deg, var(--blue-50) 0%, white 100%)",
    }}>
      <div style={{ textAlign: "center", marginBottom: "48px" }}>
        <p style={{
          letterSpacing: "3px",
          textTransform: "uppercase",
          fontSize: "0.85rem",
          color: "var(--blue-400)",
          marginBottom: "8px",
        }}>
          {t.rsvpSubtitle}
        </p>
        <h2 className="section-heading">{t.rsvpHeading}</h2>
        <div className="divider-ornament">
          <span style={{ color: "var(--gold)" }}></span>
        </div>
        <p style={{
          color: "#64748b",
          maxWidth: "450px",
          margin: "16px auto 0",
        }}>
          {t.rsvpDescription}
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        style={{
          maxWidth: "500px",
          margin: "0 auto",
          background: "white",
          padding: "40px 32px",
          borderRadius: "16px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
          display: "flex",
          flexDirection: "column",
          gap: "20px",
        }}
      >
        <div>
          <label style={labelStyle}>{t.rsvpName} *</label>
          <input
            type="text"
            name="name"
            value={form.name}
            onChange={handleChange}
            placeholder={t.rsvpNamePlaceholder}
            className="rsvp-input"
            required
          />
        </div>

        <div>
          <label style={labelStyle}>{t.rsvpAttendLabel} *</label>
          <select
            name="attendance"
            value={form.attendance}
            onChange={handleChange}
            className="rsvp-input"
            style={{ cursor: "pointer" }}
          >
            <option value="attending">{t.rsvpAttending}</option>
            <option value="not-attending">{t.rsvpNotAttending}</option>
          </select>
        </div>

        {form.attendance === "attending" && (
          <>
            <div>
              <label style={labelStyle}>{t.rsvpFoodChoice} *</label>
              <p style={noteStyle}>{t.rsvpFoodNote}</p>
              <select
                name="foodChoice"
                value={form.foodChoice}
                onChange={handleChange}
                className="rsvp-input"
                style={{ cursor: "pointer" }}
                required
              >
                <option value="" disabled>—</option>
                <option value="chicken">{t.rsvpFoodChicken}</option>
                <option value="steak">{t.rsvpFoodSteak}</option>
                <option value="vegetarian">{t.rsvpFoodVegetarian}</option>
              </select>
            </div>

            <div>
              <label style={labelStyle}>{t.rsvpPlusOne}</label>
              <p style={noteStyle}>{t.rsvpPlusOneNote}</p>
              <select
                name="plusOne"
                value={form.plusOne}
                onChange={handleChange}
                className="rsvp-input"
                style={{ cursor: "pointer" }}
              >
                <option value="no">{t.rsvpPlusOneNo}</option>
                <option value="yes">{t.rsvpPlusOneYes}</option>
              </select>
            </div>

            {form.plusOne === "yes" && (
              <>
                <div>
                  <label style={labelStyle}>{t.rsvpPlusOneName} *</label>
                  <input
                    type="text"
                    name="plusOneName"
                    value={form.plusOneName}
                    onChange={handleChange}
                    placeholder={t.rsvpPlusOneNamePlaceholder}
                    className="rsvp-input"
                    required
                  />
                </div>
                <div>
                  <label style={labelStyle}>{t.rsvpPlusOneFoodChoice} *</label>
                  <select
                    name="plusOneFoodChoice"
                    value={form.plusOneFoodChoice}
                    onChange={handleChange}
                    className="rsvp-input"
                    style={{ cursor: "pointer" }}
                    required
                  >
                    <option value="" disabled>—</option>
                    <option value="chicken">{t.rsvpFoodChicken}</option>
                    <option value="steak">{t.rsvpFoodSteak}</option>
                    <option value="vegetarian">{t.rsvpFoodVegetarian}</option>
                  </select>
                </div>
              </>
            )}

            <div>
              <label style={labelStyle}>{t.rsvpChildren}</label>
              <p style={noteStyle}>{t.rsvpChildrenNote}</p>
              <select
                name="children"
                value={form.children}
                onChange={handleChange}
                className="rsvp-input"
                style={{ cursor: "pointer" }}
              >
                <option value="0">{t.rsvpChildren0}</option>
                <option value="1">{t.rsvpChildren1}</option>
                <option value="2">{t.rsvpChildren2}</option>
                <option value="3">{t.rsvpChildren3}</option>
              </select>
            </div>

            <div>
              <label style={labelStyle}>{t.rsvpDietary}</label>
              <input
                type="text"
                name="dietary"
                value={form.dietary}
                onChange={handleChange}
                placeholder={t.rsvpDietaryPlaceholder}
                className="rsvp-input"
              />
            </div>
          </>
        )}

        {error && (
          <p style={{ color: "#dc2626", fontSize: "0.9rem", margin: 0 }}>{error}</p>
        )}

        <button
          type="submit"
          className="btn-wedding"
          style={{ marginTop: "8px", opacity: submitting ? 0.6 : 1 }}
          disabled={submitting}
        >
          {submitting ? (t.rsvpSubmitting || "Sending...") : t.rsvpSubmit}
        </button>
      </form>
    </section>
  );
}

const labelStyle = {
  display: "block",
  fontWeight: 700,
  fontSize: "0.85rem",
  color: "var(--blue-800)",
  letterSpacing: "0.5px",
  marginBottom: "6px",
  textTransform: "uppercase",
};

const noteStyle = {
  fontSize: "0.8rem",
  color: "#94a3b8",
  fontStyle: "italic",
  margin: "0 0 6px 0",
};
